import { Head, Link, usePage } from '@inertiajs/react'
import { Plus } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import MainLayout from '@/components/Layout/MainLayout'
import { Button } from '@/components/ui/Button'
import { HelpCenterHeader } from '@/components/help_center/HelpCenterHeader'
import { HelpCategoryTree } from '@/components/help_center/HelpCategoryTree'
import { HelpArticleTable } from '@/components/help_center/HelpArticleTable'
import { CategoryFormDialog } from '@/components/help_center/CategoryFormDialog'
import { useRouteParams } from '@/hooks/useRoutes'
import routes from '@/routeHelpers'
import type { CurrentUser } from '@/types/shared'
import type { HelpArticleRow, HelpCategoryNode } from './types'

interface ContentProps {
  current_user: CurrentUser
  articles: HelpArticleRow[]
  categories: HelpCategoryNode[]
  brand_primary: string | null
  brand_accent: string | null
}

export default function Content({ current_user, articles, categories, brand_primary, brand_accent }: ContentProps) {
  const { withAccount } = useRouteParams()
  const { flash } = usePage<{ flash: { notice?: string; alert?: string } }>().props
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<HelpCategoryNode | null>(null)
  const [categoryFilter, setCategoryFilter] = useState('all')

  useEffect(() => {
    if (flash?.notice) toast.success(flash.notice)
    if (flash?.alert) toast.error(flash.alert)
  }, [flash?.notice, flash?.alert])

  const openNew = () => { setEditing(null); setDialogOpen(true) }
  const openEdit = (category: HelpCategoryNode) => { setEditing(category); setDialogOpen(true) }

  // Re-resolve against fresh props so cover changes made inside the dialog
  // (which round-trip through Inertia) show up without reopening it.
  const editingCategory = editing ? categories.find(c => c.id === editing.id) ?? editing : null

  // Collapse to 'all' if the filtered category was deleted, so the table
  // isn't silently stuck filtering on an id that no longer exists.
  const effectiveFilter = categoryFilter !== 'all' && categories.some(c => c.id === categoryFilter)
    ? categoryFilter
    : 'all'

  return (
    <>
      <Head title="Help center" />
      <MainLayout user={current_user} breadcrumbs={[{ label: 'Help center' }]}>
        <div className="flex-1 overflow-y-auto p-6">
          <HelpCenterHeader
            actions={
              <Button asChild>
                <Link href={routes.helpArticles.new.path(withAccount())}>
                  <Plus className="size-4" /> New article
                </Link>
              </Button>
            }
          />

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[18rem_1fr]">
            <HelpCategoryTree
              categories={categories}
              selectedId={effectiveFilter === 'all' ? null : effectiveFilter}
              onSelect={id => setCategoryFilter(effectiveFilter === id ? 'all' : id)}
              onAdd={openNew}
              onEdit={openEdit}
            />
            <HelpArticleTable
              articles={articles}
              categories={categories}
              categoryId={effectiveFilter}
              onCategoryChange={setCategoryFilter}
            />
          </div>
        </div>
      </MainLayout>

      <CategoryFormDialog
        key={editing?.id ?? 'new'}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        category={editingCategory}
        categories={categories}
        brandPrimary={brand_primary}
        brandAccent={brand_accent}
      />
    </>
  )
}
